import { createClient } from '@supabase/supabase-js'
import type { AuthChangeEvent, Session } from '@supabase/supabase-js'
import {ConstData} from "@/datas/ConstData";

const supabase = createClient(ConstData.supabase_url, ConstData.supabase_anon_key)

// region Auth

export async function getSession() {
    const { data, error } = await supabase.auth.getSession()
    if (error) throw error
    return data
}

export async function getAccessToken(): Promise<string | null> {
    const { session } = await getSession()
    return session?.access_token ?? null
}

export function onAuthStateChange(callback: (event: AuthChangeEvent, session: Session | null) => void) {
    const { data } = supabase.auth.onAuthStateChange(callback)
    return data.subscription
}

export async function emailSignInWithOtp(email: string) {
    const { data, error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
            shouldCreateUser: true
        }
    })
    if (error) throw error
    return data
}

export async function emailVerifyOtp(email: string, otp: string) {
    const { data, error } = await supabase.auth.verifyOtp({
        email: email,
        token: otp,
        type: 'email'
    })
    if (error) throw error
    return data
}

export async function emailSignInWithPassword(email: string, password: string) {
    const {data, error} = await supabase.auth.signInWithPassword({
        email: email,
        password: password
    })
    if (error) throw error
    return data
}

export async function signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
}

export async function getUser() {
    const { data, error } = await supabase.auth.getUser()
    if (error) throw error
    return data
}
// endregion

// region Database
export async function getTbData(tbName: string, select_q: string = "*") {
    const { data, error } = await supabase.from(tbName).select(select_q)
    if (error) throw error
    return data
}

export async function isRowExist(tbName: string, colName: string, colValue: string) {
    const { data, error } = await supabase.from(tbName).select(colName).eq(colName, colValue)
    if (error) throw error
    return (data && data.length > 0)
}

export async function queryRow(tbName: string, colName: string, colValue: string, select_q: string = "*"): Promise<any> {
    const { data, error } = await supabase.from(tbName).select(select_q).eq(colName, colValue)
    if (error) throw error
    if ((data && data.length > 0)) return data[0]
    else return {}
}

export async function queryRows(tbName: string, colName: string, colValue: string, select_q: string = "*"): Promise<any[]> {
    const { data, error } = await supabase.from(tbName).select(select_q).eq(colName, colValue)
    if (error) throw error
    if (data && data.length > 0) return data
    else return []
}

export function getRawQuery(tbName: string, select_q: string = "*") {
    return supabase.from(tbName).select(select_q)
}

export async function insertRow(tbName: string, rowData: any, need_return: boolean = false, select_q: string = "*") {
    if (need_return) {
        const { data, error } = await supabase.from(tbName).insert(rowData).select(select_q)
        if (error) throw error
        return data
    } else {
        const { error } = await supabase.from(tbName).insert(rowData)
        if (error) throw error
    }
}

export async function updateData(tbName: string, colName: string, colValue: string, rowData: any) {
    const {data, error} = await supabase.from(tbName).update(rowData).eq(colName, colValue)
    return { data, error };
}

export async function upsertData(tbName: string, rowData: any) {
    const {data, error} = await supabase.from(tbName).upsert(rowData).select()
    return { data, error };
}

export async function deleteRow(tbName: string, colName: string, colValue: string) {
    const { data, error } = await supabase.from(tbName).delete().eq(colName, colValue);
    return { data, error };
}

export async function rpcAction(rpcName: string, params: any) {
    const { data, error } = await supabase.rpc(rpcName, params)
    return { data, error }
}

// endregion
